/**
 * What a screen declares to get a filter, and the four kinds it may declare (ADR to follow in
 * the screen tickets). A filter is deliberately **not** a column: `alleen beheerders` narrows on
 * a field the bevoegdhedenlijst never shows, so a descriptor riding on a column definition could
 * not express it - and `PersoonZoeker` builds its columns in a module-level helper, two layers
 * from the table.
 */

export type Filterwaarde =
  | { soort: "keuze"; waarde: string }
  | { soort: "meerkeuze"; waarden: string[] }
  | { soort: "schakelaar"; aan: boolean }
  | { soort: "periode"; van: string; tot: string };

export type Filtersoort = Filterwaarde["soort"];

export type Filteroptie = { waarde: string; label: string };

/** Keyed on the descriptor's id. A key that is absent or empty is a filter nobody set. */
export type Filterstand = Record<string, Filterwaarde | undefined>;

export type Filterdescriptor<TData> = {
  id: string;
  label: string;
  kind: Filtersoort;
  /** Only for `keuze` and `meerkeuze`. */
  opties?: Filteroptie[];
  /**
   * Set, and this filter is on before a beheerder touches anything. The control says so: a list
   * that arrives already narrowed must never read as the whole list.
   */
  standaard?: Filterwaarde;
  /**
   * Narrows in the browser. Left out, the rows are the server's answer and already narrowed -
   * which is also what turns the option counts off, since nothing here can count what it has
   * not fetched.
   */
  past?: (rij: TData, waarde: Filterwaarde) => boolean;
  /** What the control says once this filter is on. Falls back to the label. */
  zegt?: (waarde: Filterwaarde) => string;
};

export function isLeeg(waarde: Filterwaarde | undefined): boolean {
  if (!waarde) return true;
  if (waarde.soort === "keuze") return waarde.waarde === "";
  if (waarde.soort === "meerkeuze") return waarde.waarden.length === 0;
  if (waarde.soort === "schakelaar") return !waarde.aan;
  return waarde.van === "" && waarde.tot === "";
}

export function beginstand<TData>(descriptors: Filterdescriptor<TData>[]): Filterstand {
  return Object.fromEntries(
    descriptors.flatMap((d) => (d.standaard ? [[d.id, d.standaard] as const] : [])),
  );
}

export const telActief = <TData,>(
  descriptors: Filterdescriptor<TData>[],
  stand: Filterstand,
) => descriptors.filter((d) => !isLeeg(stand[d.id])).length;

/** The empty value of a kind, for a control that has to render before anything is chosen. */
export function leegVoor(kind: Filtersoort): Filterwaarde {
  switch (kind) {
    case "keuze":
      return { soort: "keuze", waarde: "" };
    case "meerkeuze":
      return { soort: "meerkeuze", waarden: [] };
    case "schakelaar":
      return { soort: "schakelaar", aan: false };
    case "periode":
      return { soort: "periode", van: "", tot: "" };
  }
}

/** Every active filter, in the browser. A descriptor without `past` is the server's to apply. */
export function pasFiltersToe<TData>(
  rijen: TData[],
  descriptors: Filterdescriptor<TData>[],
  stand: Filterstand,
): TData[] {
  const actief = descriptors
    .map((d) => ({ past: d.past, waarde: stand[d.id] }))
    .filter(
      (x): x is { past: NonNullable<typeof x.past>; waarde: Filterwaarde } =>
        x.past !== undefined && x.waarde !== undefined && !isLeeg(x.waarde),
    );

  if (actief.length === 0) return rijen;
  return rijen.filter((rij) => actief.every(({ past, waarde }) => past(rij, waarde)));
}

/**
 * How many rows one option would leave, with every *other* filter still applied. Answerable only
 * where the rows are held whole, which is why a server-driven table shows no counts at all: a
 * count per option is a query per option, per keystroke.
 */
export function telling<TData>(
  rijen: TData[],
  descriptors: Filterdescriptor<TData>[],
  stand: Filterstand,
  descriptor: Filterdescriptor<TData>,
  optie: string,
): number {
  const past = descriptor.past;
  if (!past) return 0;

  const basis = pasFiltersToe(rijen, descriptors, { ...stand, [descriptor.id]: undefined });
  const proef: Filterwaarde =
    descriptor.kind === "meerkeuze"
      ? { soort: "meerkeuze", waarden: [optie] }
      : { soort: "keuze", waarde: optie };

  return basis.filter((rij) => past(rij, proef)).length;
}

/**
 * One query parameter per filter, never one encoded blob: these ride in the route's search params
 * beside the `q` and `pagina` every one of these screens already carries, and a shared link is
 * worth more when a wedstrijdleider can read what it says. A multi-keuze repeats its key.
 *
 * The names are the descriptor ids, so an endpoint and a screen agree by construction rather than
 * by a mapping table kept in two places. A periode takes `<id>Van` and `<id>Tot`.
 */
function naarZoekparameter(id: string, waarde: Filterwaarde): Record<string, string | string[]> {
  switch (waarde.soort) {
    case "keuze":
      return { [id]: waarde.waarde };
    case "meerkeuze":
      return { [id]: waarde.waarden };
    case "schakelaar":
      return { [id]: "true" };
    case "periode": {
      const params: Record<string, string> = {};
      if (waarde.van) params[`${id}Van`] = waarde.van;
      if (waarde.tot) params[`${id}Tot`] = waarde.tot;
      return params;
    }
  }
}

export function naarZoekparameters(stand: Filterstand): Record<string, string | string[]> {
  const params: Record<string, string | string[]> = {};

  for (const [id, waarde] of Object.entries(stand)) {
    if (!waarde || isLeeg(waarde)) continue;
    Object.assign(params, naarZoekparameter(id, waarde));
  }

  return params;
}

/**
 * A filter somebody cleared. Not an empty string: the router drops those when it serialises, so
 * a cleared filter would come back as an absent key - which means *nobody touched it*, and the
 * default would quietly turn itself back on. No real value is a single dash.
 */
export const GEWIST = "-";

/**
 * One filter as a single URL-safe value, for a screen that keeps its filters in the route search
 * params. Comma-joined rather than repeated, because the router JSON-encodes an array and
 * `?klasse=%5B%22a%22%5D` is not a link anybody can read.
 */
export function naarSearchParam(waarde: Filterwaarde | undefined): string {
  if (isLeeg(waarde)) return GEWIST;

  switch (waarde!.soort) {
    case "keuze":
      return waarde!.waarde;
    case "meerkeuze":
      return waarde!.waarden.join(",");
    case "schakelaar":
      return "ja";
    case "periode":
      return `${waarde!.van},${waarde!.tot}`;
  }
}

/** The other way. See `GEWIST` for why a cleared filter is a dash rather than nothing. */
export function uitSearchParam<TData>(
  descriptor: Filterdescriptor<TData>,
  rauw: string,
): Filterwaarde {
  if (rauw === GEWIST || rauw === "") return leegVoor(descriptor.kind);

  switch (descriptor.kind) {
    case "keuze":
      return { soort: "keuze", waarde: rauw };
    case "meerkeuze":
      return { soort: "meerkeuze", waarden: rauw.split(",").filter(Boolean) };
    case "schakelaar":
      return { soort: "schakelaar", aan: rauw === "ja" };
    case "periode": {
      const [van = "", tot = ""] = rauw.split(",");
      return { soort: "periode", van, tot };
    }
  }
}
